import type { DefaultStorage } from "@urql/exchange-graphcache/default-storage";
import { makeDefaultStorage } from "@urql/exchange-graphcache/default-storage";
import { isEmpty } from "lodash";

const debug = false;

export const makeLocalStorage = () => {
  const cache = {};

  return {
    onOnline(cb: () => void) {
      window.addEventListener("online", () => {
        cb();
      });
    },
    readData() {
      return Promise.resolve().then(() => {
        const local = localStorage.getItem("data") ?? "{}";
        Object.assign(cache, JSON.parse(local));
        return cache;
      });
    },
    readMetadata() {
      return Promise.resolve().then(() => {
        const metadataJson = localStorage.getItem("metadata") ?? null;
        if (metadataJson === null) {
          return null;
        }
        // No access to the specific type from urql
        // eslint-disable-next-line
        return JSON.parse(metadataJson) as any[];
      });
    },
    writeData(delta: any) {
      return Promise.resolve().then(() => {
        Object.assign(cache, delta);
        localStorage.setItem("data", JSON.stringify(cache));
      });
    },
    writeMetadata(data: any) {
      localStorage.setItem("metadata", JSON.stringify(data));
    },
  };
};

/**
 * We override the default indexedDbStorage
 * to use it in a less intensive ressources consuming.
 * We are adding a layer to use native object to store the cache when initialised.
 * Writing data to indexedDb is bufferered to write into it every x seconds
 *
 * No need to add a cache layerfor write/read metadatas as it's called only one time
 */
const BUFFER_WRITING_TIME = 3000;
export const makeIndexedDbStorage = (): DefaultStorage => {
  const defaultStorage = makeDefaultStorage({
    idbName: "clovis", // The name of the IndexedDB database
    maxAge: 7, // The maximum age of the persisted data in days
  });

  type CachedDataType = Record<string, any>;
  let cachedData: CachedDataType | null = null;
  let bufferDeltas: CachedDataType = {};

  setInterval(() => {
    if (!isEmpty(bufferDeltas)) {
      debug && console.log("storage:bufferDeltas", bufferDeltas);
      void defaultStorage.writeData(bufferDeltas);
      bufferDeltas = {};
    }
  }, BUFFER_WRITING_TIME);

  return {
    ...defaultStorage,
    readData: () => {
      if (!cachedData) {
        return defaultStorage.readData().then((data) => {
          cachedData = data as CachedDataType;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return data as any;
        });
      }

      return Promise.resolve().then(() => {
        debug && console.log("storage:cachedData", cachedData);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return cachedData as any;
      });
    },
    writeData(delta: Record<string, any>) {
      debug && console.log("storage:writeData", delta);
      bufferDeltas = {
        ...bufferDeltas,
        ...delta,
      };

      cachedData = {
        ...(cachedData ?? {}),
        ...delta,
      };

      return Promise.resolve().then(() => {
        return cachedData;
      });
    },
  };
};
